import {
  Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges,
} from '@angular/core';

export interface ThresholdValues { lower: number; upper: number; }

type Handle = 'lower' | 'upper';

/**
 * `app-threshold-band` — barra colorida (3 faixas: inicial / intermediária / final) com
 * DUAS setas arrastáveis que definem os dois limites. Arrastar uma seta move a fronteira de
 * cor junto e atualiza o valor (entre `min` e `max`). Sem Angular Material/CDK: drag via
 * pointer events (mouse + toque); teclado (setas/Home/End) p/ a11y.
 *
 * 5 parâmetros do enunciado: `min` (valor inicial), `max` (valor final), `colorStart`,
 * `colorMiddle`, `colorEnd`. Os dois limites são two-way: `[(lower)]` / `[(upper)]`.
 */
@Component({
  selector: 'app-threshold-band',
  templateUrl: './threshold-band.component.html',
  styleUrls: ['./threshold-band.component.scss'],
})
export class ThresholdBandComponent implements OnChanges {
  @Input() min = 0;                       // valor inicial
  @Input() max = 10;                      // valor final
  @Input() colorStart = '#6aa84f';        // cor inicial (verde)
  @Input() colorMiddle = '#fff200';       // cor intermediária (amarelo)
  @Input() colorEnd = '#e8201f';          // cor final (vermelho)

  /** Casas decimais exibidas. Default 0 = só inteiros. */ @Input() decimals = 0;
  /** Granularidade do arraste (0 = contínuo). Default 1 = passos de inteiros. */ @Input() step = 1;

  @Input() lower = 3;
  @Output() lowerChange = new EventEmitter<number>();
  @Input() upper = 7;
  @Output() upperChange = new EventEmitter<number>();
  @Output() thresholdsChange = new EventEmitter<ThresholdValues>();

  @ViewChild('band') bandRef: ElementRef<HTMLElement>;

  dragging: Handle | null = null;
  private readonly onMove = (e: PointerEvent) => this.drag(e);
  private readonly onUp = () => this.endDrag();

  ngOnChanges(): void {
    // Garante min ≤ lower ≤ upper ≤ max mesmo se os inputs vierem fora de ordem.
    this.lower = this.clamp(this.lower, this.min, this.max);
    this.upper = this.clamp(this.upper, this.min, this.max);
    if (this.lower > this.upper) { const t = this.lower; this.lower = this.upper; this.upper = t; }
  }

  /** Posição (%) de um valor no trilho. */
  pct(v: number): number {
    if (this.max === this.min) { return 0; }
    return ((this.clamp(v, this.min, this.max) - this.min) / (this.max - this.min)) * 100;
  }

  /** Texto do valor (casas decimais + vírgula decimal pt-BR). */
  fmt(v: number): string {
    return v.toFixed(this.decimals).replace('.', ',');
  }

  startDrag(which: Handle, ev: PointerEvent): void {
    ev.preventDefault();
    this.dragging = which;
    document.addEventListener('pointermove', this.onMove);
    document.addEventListener('pointerup', this.onUp);
  }

  onKey(which: Handle, ev: KeyboardEvent): void {
    const inc = this.step || (this.max - this.min) / 100;
    let delta = 0;
    switch (ev.key) {
      case 'ArrowRight': case 'ArrowUp': delta = inc; break;
      case 'ArrowLeft': case 'ArrowDown': delta = -inc; break;
      case 'Home': delta = -(this.max - this.min); break;   // até o limite mínimo possível
      case 'End': delta = this.max - this.min; break;        // até o limite máximo possível
      default: return;
    }
    ev.preventDefault();
    this.apply(which, (which === 'lower' ? this.lower : this.upper) + delta);
  }

  private drag(ev: PointerEvent): void {
    if (!this.dragging || !this.bandRef) { return; }
    const r = this.bandRef.nativeElement.getBoundingClientRect();
    const ratio = r.width ? (ev.clientX - r.left) / r.width : 0;
    this.apply(this.dragging, this.min + ratio * (this.max - this.min));
  }

  /** Aplica o valor a um handle, com snap + clamp (um não ultrapassa o outro). */
  private apply(which: Handle, raw: number): void {
    const v = this.snap(this.clamp(raw, this.min, this.max));
    if (which === 'lower') {
      this.lower = Math.min(v, this.upper);
      this.lowerChange.emit(this.lower);
    } else {
      this.upper = Math.max(v, this.lower);
      this.upperChange.emit(this.upper);
    }
    this.thresholdsChange.emit({ lower: this.lower, upper: this.upper });
  }

  private endDrag(): void {
    this.dragging = null;
    document.removeEventListener('pointermove', this.onMove);
    document.removeEventListener('pointerup', this.onUp);
  }

  private snap(v: number): number {
    if (!this.step) { return v; }
    return this.clamp(this.min + Math.round((v - this.min) / this.step) * this.step, this.min, this.max);
  }

  private clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, v));
  }
}
